// src/BookingLedger.js
// The money on one booking, line by line: subtotal, discount, tax, what was
// paid, LIVLIFE's commission, Stripe's processing fee, refunds, chargebacks
// and manual adjustments. Reads the booking_ledger view and, for the history,
// booking_ledger_entries (both admin-only by RLS).
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button, TextField, Alert, Divider } from '@mui/material';
import { supabase } from './supabaseClient';
import { ink, surface } from './designTokens';

const usd = (cents) =>
  cents == null ? 'Not collected' : (Number(cents) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const KIND_LABEL = {
  payment: 'Payment',
  commission: 'Commission',
  processing_fee: 'Processing fee',
  refund: 'Refund',
  chargeback: 'Chargeback',
  chargeback_reversal: 'Chargeback won',
  adjustment: 'Adjustment',
};

export default function BookingLedger({ booking }) {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!booking?.id) return;
    const [{ data: s }, { data: e }] = await Promise.all([
      supabase.from('booking_ledger').select('*').eq('booking_id', booking.id).maybeSingle(),
      supabase
        .from('booking_ledger_entries')
        .select('*')
        .eq('booking_id', booking.id)
        .order('created_at', { ascending: true }),
    ]);
    setSummary(s || null);
    setEntries(e || []);
  }, [booking?.id]);

  useEffect(() => { load(); }, [load]);

  const [discount, setDiscount] = useState('');
  const [discountNote, setDiscountNote] = useState('');

  // Separate from an adjustment on purpose. An adjustment writes a ledger row
  // after the fact; a discount changes what the client is actually charged, so
  // it only works before payment and create-payment-intent reads it.
  const setBookingDiscount = async () => {
    const dollars = Number(discount);
    if (!Number.isFinite(dollars) || dollars < 0 || !discountNote.trim()) {
      setMessage({ severity: 'warning', text: 'A discount needs an amount and a reason.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    const cents = Math.round(dollars * 100);
    const { error } = await supabase.rpc('admin_set_booking_discount', {
      p_booking_id: booking.id,
      p_amount_cents: cents,
      p_note: discountNote.trim(),
    });
    if (error) {
      setMessage({ severity: 'error', text: error.message });
    } else {
      try {
        await supabase.rpc('write_admin_audit', {
          p_action: 'booking_discount',
          p_entity_type: 'booking',
          p_entity_id: booking.id,
          p_payload: { amount_cents: cents, note: discountNote.trim() },
        });
      } catch (_) { /* the booking row records who set it and why */ }
      setDiscount('');
      setDiscountNote('');
      setMessage({ severity: 'success', text: 'Discount set. The client will be charged the reduced amount.' });
      await load();
    }
    setSaving(false);
  };

  const addAdjustment = async () => {
    const dollars = Number(amount);
    if (!Number.isFinite(dollars) || dollars === 0 || !note.trim()) {
      setMessage({ severity: 'warning', text: 'An adjustment needs a non-zero amount and a note.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    const cents = Math.round(dollars * 100);
    const { error } = await supabase.rpc('admin_add_booking_adjustment', {
      p_booking_id: booking.id,
      p_amount_cents: cents,
      p_note: note.trim(),
    });
    if (error) {
      setMessage({ severity: 'error', text: `Could not add: ${error.message}` });
    } else {
      try {
        await supabase.rpc('write_admin_audit', {
          p_action: 'ledger_adjustment',
          p_entity_type: 'booking',
          p_entity_id: booking.id,
          p_payload: { amount_cents: cents, note: note.trim() },
        });
      } catch (_) { /* the ledger row itself records who and when */ }
      setAmount('');
      setNote('');
      await load();
    }
    setSaving(false);
  };

  const rows = summary && [
    ['Subtotal', usd(summary.subtotal_cents)],
    ['Discount', usd(summary.discount_cents)],
    ['Tax', summary.tax_cents == null ? 'Not collected' : usd(summary.tax_cents)],
    ['Amount paid', usd(summary.amount_paid_cents)],
    ['Commission (LIVLIFE)', usd(summary.commission_cents)],
    ['Processing fee (Stripe)', usd(summary.processing_fee_cents)],
    ['Refunded', usd(summary.refunded_cents)],
    ['Charged back', usd(summary.chargeback_cents)],
    ['Adjustments', usd(summary.adjustment_cents)],
  ];

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Ledger</Typography>
      {!summary ? (
        <Typography variant="body2" sx={{ color: ink[500] }}>No ledger data yet.</Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, p: 1.5, bgcolor: surface.sunken, borderRadius: 1 }}>
          {rows.map(([label, value]) => (
            <Box key={label}>
              <Typography variant="caption" sx={{ color: ink[500], display: 'block' }}>{label}</Typography>
              <Typography variant="body2">{value}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {entries.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          {entries.map((e) => (
            <Box key={e.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
              <Typography variant="caption" sx={{ color: ink[700] }}>
                {new Date(e.created_at).toLocaleString()} · {KIND_LABEL[e.kind] || e.kind}
                {e.note ? ` — ${e.note}` : ''}
              </Typography>
              <Typography variant="caption">{usd(e.amount_cents)}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" sx={{ color: ink[500], display: 'block', mb: 1 }}>
        Set a discount. This reduces what the client is charged, so it only works before payment —
        after that, refund instead.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField size="small" label="Discount ($)" value={discount} onChange={(e) => setDiscount(e.target.value)} sx={{ width: 130 }} />
        <TextField size="small" label="Reason" value={discountNote} onChange={(e) => setDiscountNote(e.target.value)} fullWidth />
        <Button variant="outlined" onClick={setBookingDiscount} disabled={saving}>Apply</Button>
      </Box>

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" sx={{ color: ink[500], display: 'block', mb: 1 }}>
        Record a manual adjustment (use a negative amount to reduce). This writes a ledger row only; it does not move money in Stripe.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField size="small" label="Amount ($)" value={amount} onChange={(e) => setAmount(e.target.value)} sx={{ width: 130 }} />
        <TextField size="small" label="Reason" value={note} onChange={(e) => setNote(e.target.value)} fullWidth />
        <Button variant="outlined" onClick={addAdjustment} disabled={saving}>Add</Button>
      </Box>
      {message && <Alert severity={message.severity} sx={{ mt: 1 }}>{message.text}</Alert>}
    </Box>
  );
}
